import { useState, useEffect } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover"
import { Button } from "../components/ui/button"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

function parseValue(raw: unknown): { year: number; month: number } | null {
  if (typeof raw === "string" && /^\d{4}-\d{2}$/.test(raw)) {
    const [y, m] = raw.split("-").map(Number)
    return { year: y, month: m }
  }
  return null
}

export function MonthPickerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const [open, setOpen] = useState(false)

  const now = new Date()
  const rawValue = element.bind ? resolveSymbol(element.bind.startsWith("#{") ? element.bind : `#{${element.bind}}`) : undefined
  const parsed = parseValue(rawValue)
  const viewYear = parsed?.year ?? now.getFullYear()
  const [displayYear, setDisplayYear] = useState(viewYear)

  // Sync displayYear when bound value changes (e.g., after init compute or month selection)
  useEffect(() => {
    if (parsed) setDisplayYear(parsed.year)
  }, [parsed?.year])

  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : "Pick a month"

  const disabled = element.disabled === true

  const displayText = parsed
    ? `${MONTH_NAMES[parsed.month - 1]} ${parsed.year}`
    : placeholder

  const handleMonthSelect = (monthIndex: number) => {
    const mm = String(monthIndex + 1).padStart(2, "0")
    dispatchEvent(element.id, "change", {
      year: displayYear,
      month: monthIndex + 1,
      value: `${displayYear}-${mm}`,
      yearMonth: `${displayYear}${mm}`,
    })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* base-ui uses `render` for composition; original `asChild` was a Radix leftover. */}
      <PopoverTrigger
        disabled={disabled}
        aria-label={element.ariaLabel ?? placeholder}
        data-testid={`month-picker-${element.id}`}
        render={
          <Button
            variant="outline"
            className={cn(
              "w-full justify-between font-normal",
              !parsed && "text-muted-foreground",
              stripPresetConflicts(element.className),
            )}
          >
            <span>{displayText}</span>
            <svg
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </Button>
        }
      />
      <PopoverContent className="w-64 p-3" align="start">
        <div className="mb-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setDisplayYear((y) => y - 1)}
            aria-label="Previous year"
          >
            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Button>
          <div className="text-sm font-bold" data-testid="year-display">
            {displayYear}
          </div>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() => setDisplayYear((y) => y + 1)}
            aria-label="Next year"
          >
            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {MONTHS.map((label, index) => {
            const isSelected =
              parsed !== null &&
              parsed.month === index + 1 &&
              parsed.year === displayYear
            const isCurrent =
              now.getMonth() === index &&
              now.getFullYear() === displayYear

            return (
              <button
                key={label}
                type="button"
                className={cn(
                  "h-9 rounded-md text-xs font-semibold",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent hover:text-accent-foreground",
                  isCurrent &&
                    !isSelected &&
                    "ring-1 ring-ring/40 text-foreground"
                )}
                onClick={() => handleMonthSelect(index)}
              >
                {label}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
